import { LANGUAGE_CODES } from 'constants/languageList'

/**
 * Label shown on the UI for a language code.
 * A tenant can rename codes through `languageCodeDisplayMap` in
 * public/config/<tenant>.json, e.g. { "EN-SE": "EN" }.
 *
 * @param {string} languageCode
 * @param {Object} [generalConfig] Tenant config (`state.uiConfig.general`)
 * @returns {string}
 */
export const getLanguageCodeLabel = (languageCode, generalConfig) => {
  const code = languageCode?.toString()?.toUpperCase()
  if (!code) return ''
  return generalConfig?.languageCodeDisplayMap?.[code] || code
}

/**
 * Human-readable name for a language code, resolved in this order:
 * 1. `LANGUAGE_NAME_<active>` translation object keyed by code
 * 2. `LANGUAGE_NAME_<code>` translation (string, or object keyed by the active code)
 * 3. `LANGUAGE_CODES` static map
 * 4. The code itself, through getLanguageCodeLabel (tenant override)
 *
 * @param {string} item Language code to label
 * @param {string} activeLanguageCode Currently selected language code
 * @param {Object} languageData Translations
 * @param {Object} [generalConfig] Tenant config (`state.uiConfig.general`)
 * @returns {string}
 */
export const getLanguageDisplayName = (item, activeLanguageCode, languageData, generalConfig) => {
  const activeCode = (activeLanguageCode || 'EN')?.toString()?.toUpperCase()
  const itemCode = item?.toString()?.toUpperCase()

  if (!itemCode) return ''

  const codeLabel = getLanguageCodeLabel(itemCode, generalConfig)
  const activeNames = languageData?.[`LANGUAGE_NAME_${activeCode}`]

  if (activeNames) {
    if (typeof activeNames === 'object') {
      return (
        activeNames[itemCode] ||
        languageData?.[`LANGUAGE_NAME_${itemCode}`]?.[activeCode] ||
        LANGUAGE_CODES?.[itemCode] ||
        codeLabel
      )
    }
    if (activeCode === itemCode && typeof activeNames === 'string' && activeNames.trim() !== '') {
      return activeNames
    }
  }

  const directName = languageData?.[`LANGUAGE_NAME_${itemCode}`]
  if (typeof directName === 'string' && directName.trim() !== '') {
    return directName
  }
  if (typeof directName === 'object' && directName !== null) {
    return directName[activeCode] || LANGUAGE_CODES?.[itemCode] || codeLabel
  }

  return LANGUAGE_CODES?.[itemCode] || codeLabel
}

/**
 * Languages allowed on the current domain when the tenant config overrides the
 * backend list (`language.isOverrideLanguage` in public/config/<tenant>.json).
 * The per-domain config list decides the order, so its first entry is the site
 * default whenever REACT_APP_DEFAULT_LANGUAGE is not allowed there (AMGP-4182).
 * The backend `allowedLanguages` only decides which codes may appear at all.
 *
 * @param {Array<string>} backendLanguages `allowedLanguages` from get-languages
 * @param {Array<string>} configLanguages Per-domain list from the tenant config
 * @returns {Array<string>} Config codes the backend allows, in config order, without duplicates
 */
export const filterLanguagesByConfig = (backendLanguages, configLanguages) => {
  if (!Array.isArray(backendLanguages) || !Array.isArray(configLanguages)) return []
  return [...new Set(configLanguages)].filter(languageCode => backendLanguages.includes(languageCode))
}

/**
 * Checks whether a translation value is valid.
 * A translation value is considered valid if it is not null, not undefined,
 * and not an empty string or a string consisting solely of whitespace.
 * Non-empty objects, numbers, and booleans are also considered valid.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isValidTranslationValue = (value) => {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (typeof value === 'object') {
    return !Array.isArray(value) && Object.keys(value).length > 0
  }
  return true
}

/**
 * Merges fallback base translations and primary/API translations.
 *
 * Requirements:
 * 1. Primary translations take precedence over base translations when valid.
 * 2. If a key is present in primary translations but has an invalid value
 *    (null, undefined, empty string, or whitespace-only), it falls back to
 *    the base translation.
 * 3. Any key present in the merged output must have a valid value.
 *    Keys with null, undefined, empty string, or whitespace-only in both
 *    sources are excluded.
 *
 * @param {Object} [baseTranslations={}] Fallback translations (e.g. from default.json)
 * @param {Object} [primaryLanguageData={}] Primary translations (e.g. from API)
 * @returns {Object} Sanitized merged translations dictionary
 */
export const mergeLanguageData = (baseTranslations = {}, primaryLanguageData = {}) => {
  const merged = {}

  const safeBase = (baseTranslations && typeof baseTranslations === 'object' && !Array.isArray(baseTranslations))
    ? baseTranslations
    : {}
  const safePrimary = (primaryLanguageData && typeof primaryLanguageData === 'object' && !Array.isArray(primaryLanguageData))
    ? primaryLanguageData
    : {}

  for (const [key, value] of Object.entries(safeBase)) {
    if (isValidTranslationValue(value)) {
      merged[key] = typeof value === 'string' ? value.trim() : value
    }
  }

  for (const [key, value] of Object.entries(safePrimary)) {
    if (isValidTranslationValue(value)) {
      merged[key] = typeof value === 'string' ? value.trim() : value
    }
  }

  return merged
}
